/*
  bf_turn.ts
  Copyright (c) 2013-present Human Cube Inc.

  Does all turn calculation, etc.
  Really this file is the main entry point for callers to this folder of code.
  Provides game starting and complete turn progression functionality.

  NOTES:
    - Must all be synchronous and not involve any external DB, file access or external user data.
      These restrictions are to allow it to be re-used in other environments such as the client.
    - setHitmapBitmapGetterFunction(handle) in bf_hitmap.js must be called before utilizing any
      other functions.
*/

import BF from './bfconst1';
import bfH from './bf_helper';
import { checkForVictory } from './internal/bf_turnvictory';
import { Game, PlayerNormalTurn, PlayerPickTurn } from './gameTypes';
import { generatePickCapital, generatePickZone } from './bf_defaultpick';
import { doTurnPurchase } from './internal/bf_turnpurchase';
import { doPlayerElimination } from './internal/bf_turneliminate';
import { doAircraftLanding } from './internal/bf_turnlanding';
import { doTakeover } from './internal/bf_turntakeover';
import { getNewZoneXY } from './bf_hitmap';
import bfTurnReplay from './internal/bf_turnreplay';
import { doArrange, doMove, setAllMovementPoints } from './internal/bf_turnmove';
import { doCombat, doEliminateAllMoveTerminateForces } from './internal/bf_turncombat';
import { do123123123AutoPick, do123321321AutoPick, doDistributedAutoPick, doScatteredAutoPick } from './bf_gameautopick';
import { doRetreatValuesCleanUp } from './internal/bf_retreatUtils';


export function executeTurn (
  game: Game,
  turns: (PlayerPickTurn | PlayerNormalTurn | undefined)[],
): number {
  // Advances the game to the next turn. Does all calculations and fills replay, etc.
  // Returns 0 on success, non-0 on error.

  game.replay = {
    // Clear the replay data and set the start of new data for it for this turn.
    turn: game.turn,
    required: game.required,
    noWait: game.noWait,
    submitted: game.submitted,
    autoPlay: game.autoPlay
  };

  if(!Array.isArray(game.playerAutoTotal) || !Array.isArray(game.playerAutoCurrent) ||
      !Array.isArray(game.playerMissedTotal) || !Array.isArray(game.playerMissedCurrent)) {
    game.playerAutoTotal = [];
    game.playerAutoCurrent = [];
    game.playerMissedTotal = [];
    game.playerMissedCurrent = [];
    for(let i = 0; i <= game.players; i++) { // [0..players]
      game.playerAutoTotal[i] = 0;
      game.playerAutoCurrent[i] = 0;
      game.playerMissedTotal[i] = 0;
      game.playerMissedCurrent[i] = 0;
    }
  }

  let nonAutoplayedHumanCount = 0;

  for(let i = 1; i <= game.players; i++) {   // [1..players]
    const bit = 1 << i;
    if(game.required & bit) {
      if(turns[i]) {
        if(game.playerFlags[i] & BF.PLAYER_FLAG_HUMAN) {
          if(turns[i]!.source === BF.TURN_SOURCE_MANUAL) {
            // Human submitted turn.
            nonAutoplayedHumanCount++;
            game.playerMissedCurrent[i] = 0;
            game.playerAutoCurrent[i] = 0;
          }
          else if(turns[i]!.source === BF.TURN_SOURCE_DEFAULT) {
            // Missed a turn, but we just generated a default turn now during turn calc.
            game.playerMissedCurrent[i]++;
            game.playerMissedTotal[i]++;
            game.playerAutoCurrent[i] = 0;
          }
          else {  // Auto-played turn.
            game.playerMissedCurrent[i] = 0;
            game.playerAutoCurrent[i]++;
            game.playerAutoTotal[i]++;
          }
        }
        else {  // Computer player, just mark everything as fine.
          game.playerMissedCurrent[i] = 0;
          game.playerAutoCurrent[i] = 0;
        }
      }
      else {  // No turn found (though there always should be), treat as default generated turn:
        game.playerMissedCurrent[i]++;
        game.playerMissedTotal[i]++;
        game.playerAutoCurrent[i] = 0;
      }
    }
  }

  if(nonAutoplayedHumanCount > 0) {
    game.autoCurrent = 0;
  }
  else {
    game.autoCurrent++;
    game.autoTotal++;
  }

  if( game.turn < BF.TURN_REGISTRATION ) {  // pre-registration (displayed)
    return 0;
  }
  if( game.turn < BF.TURN_PICK_ZONE ) {  // registering
    return 0;
  }
  if( game.turn < BF.TURN_PICK_CAPITAL ) {  // pick zone
    return doPickZoneTurn(game, turns as PlayerPickTurn[]);
  }
  if( game.turn === BF.TURN_PICK_CAPITAL ) {  // pick capital
    return doPickCapitalTurn(game, turns as PlayerPickTurn[]);
  }
  if( game.turn >= BF.TURN_NORMAL ) {  // Normal turn
    return doNormalTurn(game, turns as PlayerNormalTurn[]);
  }

  return 1;  // Unknown turn state.
}

export function createInitialForces (
  game: Game,
  startingForces: number[][] | undefined,
  navyZone: number[] | undefined,
) {
  // Create all the initial forces. Modifies Game object.

  // First clear all forces:
  game.nextForceID = 1;
  game.force = [];
  for(let p = 0; p <= game.players; p++) {  // All players and 0 player.
    game.force.push([]);
  }

  if(!Array.isArray(startingForces)) {
    return;  // No starting forces specified.
  }

  // Then create any initial forces:
  for(let i = 1; i <= game.zones; i++) {
    if(!bfH.isLand(game, i)) {
      continue;
    }
    const owner = game.zoneOwner[i] ? game.zoneOwner[i] : 0;
    for(let j = 0; j < startingForces[i].length; j++) {
      if(game.forceID[j]) {  // If the forceType is valid.
        const count = startingForces[i][j];
        if(count > 0) {
          if(game.forceFlags[j] & BF.FORCE_FLAG_STARTS_ON_LAND) {
            for(let k = 0; k < count; k++) {  // Add the force to land.
              const pos = getNewZoneXY(game, i);
              bfH.createNewForce(game, owner, j, i, i, pos.x, pos.y);
            }
          }
          else if(game.forceFlags[j] & BF.FORCE_FLAG_STARTS_ON_WATER) {
            if(Array.isArray(navyZone) && navyZone[i]) {
              for(let k = 0; k < count; k++) {  // Add the force to water.
                const pos = getNewZoneXY(game, navyZone[i]);
                bfH.createNewForce(game, owner, j, navyZone[i], navyZone[i], pos.x, pos.y);
              }
            }
            else {
              // If navy zone not specified then ignore initial water force creation.
            }
          }
        }
      }
    }
  }
}

function assignAutomaticCapitalForces (game: Game) {
  // Supports both GAME_FLAG_FACTORY_AT_CAPITAL and game.addToCapital[] values.
  const addToCapital =
    game.addToCapital ? game.addToCapital : Array<number>(game.forceID.length).fill(0);

  if( game.flags & BF.GAME_FLAG_FACTORY_AT_CAPITAL ) {
    const factoryTypeIndex = bfH.getForceTypeIndexByName(game, 'Factory');
    if( factoryTypeIndex >= 0 ) {
      if(addToCapital[factoryTypeIndex] === 0) {
        addToCapital[factoryTypeIndex] = 1;
      }
    }
    else {
      // Cannot find appropriate force type for a factory.
    }
  }

  for(let playerIndex = 1; playerIndex <= game.players; playerIndex++) {
    const capitalID = game.capital[playerIndex];
    if( capitalID > 0 ) {
      for(let forceTypeIndex = 0; forceTypeIndex < addToCapital.length; forceTypeIndex++) {
        for(let count = 0; count < addToCapital[forceTypeIndex]; count++) {
          if( !(game.forceFlags[forceTypeIndex] & BF.FORCE_FLAG_PROD_MAX_ONE_PER_ZONE) ||
              !bfH.zoneHasPlayerForce(game, capitalID, playerIndex, forceTypeIndex) ) {
            const pos = getNewZoneXY(game, capitalID);
            bfH.createNewForce(
              game, playerIndex, forceTypeIndex, capitalID, capitalID, pos.x, pos.y,
            );

            // TODO:  Add replay...

          }
        }
      }
    }
  }
}


function collectIncome (game: Game, initialFlag: boolean) {
  for(let i = 1; i <= game.players; i++) {
    if( game.capital[i] > 0 && game.zoneOwner[game.capital[i]] === i ) {
      // Player owns their capital.
      // Collect income for all the zones they own:
      for(let j = 1; j <= game.zones; j++) {
        if( game.zoneOwner[j] === i ) {
          game.cash[i] += game.zoneIncome[j];
        }
      }
    }
    else {
      // Player currently does not own their capital.
      if( !initialFlag ) {
        // Not the first turn, so wipe out their cash if they do not own their capital.
        game.cash[i] = 0;
      }
    }
  }
}


function setTurnTimeLimit (game: Game) {
  // Set the turn time limit appropriate for the turn, play times and maximum game time.
  if( game.state !== BF.GAME_STATE_STARTED ) {
    game.timeLimit = '';  // No timelimit.
    return;
  }

  if( game.turn >= BF.TURN_NORMAL ) {
    game.timeLimit = Date.now() + (game.timeTurn * 1000);  // epoch milliseconds.
  }
  else if( game.turn >= BF.TURN_PICK_CAPITAL ) {
    game.timeLimit = Date.now() + (game.timeCapitalPicking * 1000);  // epoch ms.
  }
  else {
    game.timeLimit = Date.now() + (game.timeZonePicking * 1000);  // epoch ms.
  }

  if( game.playTimes && Array.isArray(game.playTimes) ) {
    // TODO:  Low priority:  Take play times into account.  (game.playTimes[])...
  }

  if( game.maxDuration && game.maxDuration > 0 && game.started) {
    const maxDurationMS = game.maxDuration * 1000;
    // Just incase it is the old string date format (new games will already be in epoch ms format):
    const startedEpochMS = (new Date(game.started)).getTime();
    if(game.timeLimit - startedEpochMS > maxDurationMS) {
      game.timeLimit = startedEpochMS + maxDurationMS;  // epoch milliseconds.
    }
  }
}


function setTurnRequiredFlags (game: Game) {
  // Set the flags for which player turns are required this turn.
  game.submitted = 0;
  game.required = 0;

  if( game.state !== BF.GAME_STATE_STARTED )
      return;   // Game is finished or cancelled or has not started yet.

  if( game.turn >= BF.TURN_NORMAL ) {
    // All remaining players need to submit a turn:
    for(let i = 1; i <= game.players; i++)
      if( !game.finished[i][0] ) // not eliminated yet.
        game.required |= (1<<i);
  }
  else if( game.turn >= BF.TURN_PICK_CAPITAL ) {
    for(let i = 1; i <= game.players; i++)
      if( game.capital[i] <= 0 ) // not selected yet.
        game.required |= (1<<i);
  }
  else if( game.turn >= BF.TURN_PICK_ZONE ) {
    // Single person's pick zone turn.
    const pickingRound = game.turn - BF.TURN_PICK_ZONE;  // 0 based.
    if( game.pickingOrder === BF.PICKING_ORDER_123123123 ) {
      // 123123123123
      game.required = (1 << ((pickingRound % game.players) + 1));
    }
    else // if( game.pickingOrder === BF.PICKING_ORDER_123321321 )
    {  // also default to this if not 123123123.
      // 123321321321
      if( pickingRound < game.players ) {
        game.required = (1 << (pickingRound + 1));
      }
      else {
        game.required = (1 << (game.players - (pickingRound % game.players)));
      }
    }
  }
  else {
    // bad.
  }

  // Fill the game.noWait values for players that have missed too many turns.
  // Note, auto-play will not trigger noWait.
  game.noWait = 0;
  if(Array.isArray(game.playerMissedCurrent)) {
    for(let i = 1; i <= game.players; i++) { // All players.
      let bit = (1 << i);
      if(game.required & bit)
        if(game.playerMissedCurrent[i] >= game.missedAllowed)
          game.noWait |= bit;
    }
  }
}


function setMaxBuy (game: Game) {
  // Calculate and store the maxBuy array values for the game.
  // maxBuy is the maximum number of forces each player may purchase this turn.
  if( game.state !== BF.GAME_STATE_STARTED ) {
      // If not started, cancelled or finished, just do all 0's:
      game.maxBuy = [0];
      for(let i = 1; i <= game.players; i++)
          game.maxBuy.push(0);
      return;
  }

  let playersStillPlaying = 0;
  let reducer = game.force[0].length;

  for(let i = 1; i <= game.players; i++)  // All players.
      if( !game.finished[i][0] )
          playersStillPlaying++;
  if( playersStillPlaying < 1 )
      playersStillPlaying = 1;

  // Take into account any players that might be over their fair share already.
  // Reduce global pool accordingly.
  let rawMax = Math.floor((BF.MAX_FORCES - reducer) / playersStillPlaying);
  for(let i = 1; i <= game.players; i++)  // All players.
      if( game.force[i].length > rawMax )
          reducer += game.force[i].length - rawMax;
  // Total maxBuy pool is max - neutral forces - overages.  Then divide by players.
  let maxBuy = Math.floor((BF.MAX_FORCES - reducer) / playersStillPlaying);

  game.maxBuy = [0];
  for(let i = 1; i <= game.players; i++) {
      if( game.force[i].length < maxBuy )
          game.maxBuy.push(maxBuy - game.force[i].length);
      else game.maxBuy.push(0);
  }
}


export function startGame (game: Game): number {
  // Ends registration and starts a game.
  // Returns 0 on success and is always successful.
  game.state = BF.GAME_STATE_STARTING;

  if(bfH.existsUnpickedZone(game) &&
      game.pickingOrder !== BF.PICKING_ORDER_NO_PICKING &&
      game.pickingOrder !== BF.PICKING_ORDER_123321321 &&
      game.pickingOrder !== BF.PICKING_ORDER_123123123) {
    let startingForces: number[][] = [];
    if(game.startingForces) {
      startingForces = game.startingForces;
    }
    else {
      // Reverse engineer starting forces info, to be used for auto-picking.
      for(let z = 0; z <= game.zones; z++) {  // including 0 zone.
        startingForces[z] = Array<number>(game.forceID.length).fill(0);
      }
      for(let p = 0; p < game.players; p++) {  // including 0 player.
        for(let f = 0; f < game.force[p].length; f++) {  // including 0 player.
          const force = game.force[p][f];
          const zoneID = force[BF.FORCE_INDEX_ZONE];
          const forceTypeIndex = force[BF.FORCE_INDEX_TYPE];
          if(zoneID > 0) {
            if(Array.isArray(game.navyZone)) {
              const parentZoneID = game.navyZone.indexOf(zoneID);
              if(parentZoneID > 0) {
                // This force is in a navy zone, so credit the parent land zone with this force.
                startingForces[parentZoneID][forceTypeIndex]++;
                continue;
              }
            }
            startingForces[zoneID][forceTypeIndex]++;
          }
        }
      }
    }

    switch(game.pickingOrder) {
      case BF.PICKING_ORDER_DISTRIBUTED:
        doDistributedAutoPick(game, startingForces);
        break;
      case BF.PICKING_ORDER_AUTO_123321321:
        do123321321AutoPick(game, startingForces);
        break;
      case BF.PICKING_ORDER_AUTO_123123123:
        do123123123AutoPick(game, startingForces);
        break;
      case BF.PICKING_ORDER_SCATTERED:
        doScatteredAutoPick(game, startingForces);
        break;
      default:
        doScatteredAutoPick(game, startingForces);  // default to scattered.
        break;
    }

    // Update all starting forces owner to the new auto-picked owner of their zones:
    for(let f = game.force[0].length - 1; f >= 0; f--) {
      const force = game.force[0][f];
      let ownerZoneID = force[BF.FORCE_INDEX_ZONE];
      if(ownerZoneID > 0) {
        if( bfH.isWater(game, ownerZoneID) ) {
          // Determine water forces owner to the land zone who have the navyZone for the given zone:
          if(game.navyZone && Array.isArray(game.navyZone)) {
            const index = game.navyZone.indexOf(ownerZoneID);
            if(index > 0) {
              ownerZoneID = index;
            }
          }
        }
        const playerIndex = game.zoneOwner[ownerZoneID];
        if(playerIndex > 0) {
          game.force[0].splice(f, 1);  // Remove from 0 player.
          game.force[playerIndex].push(force);  // Assign to this player.
        }
      }
    }
  }

  if(bfH.existsUnpickedZone(game) &&
      game.pickingOrder === BF.PICKING_ORDER_123321321 && 
      game.pickingOrder === BF.PICKING_ORDER_123123123) {
    game.turn = BF.TURN_PICK_ZONE;  // Pick Zone turn.
  }
  else {
    // All zones that could/should be picked are picked, check if capitals
    // need to be picked.
    let unpickedCapitalCount = 0;
    for(let i = 1; i <= game.players; i++) {
      if( !game.capital[i] ) {
        unpickedCapitalCount++;
      }
    }
    if( unpickedCapitalCount > 0 ) {
      game.turn = BF.TURN_PICK_CAPITAL;  // Pick Capital turn.
    }
    else {
      // All capitals are already selected, so go directly to the first turn.
      assignAutomaticCapitalForces(game);
      collectIncome(game, true);
      game.turn = BF.TURN_NORMAL;   // Normal Turn.
    }
  }

  game.started = Date.now();  // All new date formats we store as epoch milliseconds.
  game.state = BF.GAME_STATE_STARTED;
  setMaxBuy(game);
  setTurnRequiredFlags(game);
  setTurnTimeLimit(game);
  return 0;  // success.
}


function doPickZoneTurn (game: Game, turns: PlayerPickTurn[]): number {
  // Always successful and always returns 0.
  for(let i = 1; i <= game.players; i++) {
    if( game.required & (1 << i) ) {    // This player has a pick this turn.
      let pickID = turns[i].pick;

      if( !pickID || pickID <= 0 || pickID > game.zones || !bfH.isLand(game, pickID) ||
          game.zoneOwner[pickID] > 0 ) {
        // Pick was invalid or missing, select a default one.
        pickID = generatePickZone(game, i).pick;
      }
      if( !pickID || pickID <= 0 || pickID > game.zones || !bfH.isLand(game, pickID) ||
          game.zoneOwner[pickID] > 0 ) {
        console.error('BAD: Default pick was still invalid.');
      }
      else {
        bfTurnReplay.pickZone(game, i, pickID);
        game.zoneOwner[pickID] = i;
        game.zoneOwnerPrev[pickID] = i;

        // Assign player 0 forces for this zone and it's navy zone to this player:
        const playerIndex = i;
        const zoneID = pickID;
        const navyZoneID = game.navyZone ? game.navyZone[zoneID] : 0;
        for(let f = game.force[0].length - 1; f >= 0; f--) {
          const force = game.force[0][f];
          if(force[BF.FORCE_INDEX_ZONE] === zoneID || force[BF.FORCE_INDEX_ZONE] === navyZoneID ) {
            game.force[0].splice(f, 1);  // Remove from 0 player.
            game.force[playerIndex].push(force);  // Assign to this player.
          }
        }
      }
    }
  }

  // Progress to next turn:
  for(let j = 1; j <= game.zones; j++) {
    if( game.zoneOwner[j] <= 0 ) {   // Unpicked.
      if( bfH.isLand(game, j) ) {
        // There is still at least one zone unpicked.
        game.turn++;
        setMaxBuy(game);
        setTurnRequiredFlags(game);
        setTurnTimeLimit(game);
        return 0;  // success.
      }
    }
  }

  for(let i = 1; i <= game.players; i++) {
    if( game.capital[i] <= 0 ) {
      // There is still at least one capital unpicked.
      game.turn = BF.TURN_PICK_CAPITAL;   // Pick capital.
      setMaxBuy(game);
      setTurnRequiredFlags(game);
      setTurnTimeLimit(game);
      return 0;  // success.
    }
  }

  game.turn = BF.TURN_NORMAL;   // Normal turn.
  assignAutomaticCapitalForces(game);
  collectIncome(game, true);
  setMaxBuy(game);
  setTurnRequiredFlags(game);
  setTurnTimeLimit(game);
  return 0;  // success.
}


function doPickCapitalTurn (game: Game, turns: PlayerPickTurn[]): number {
  // Always successful and always returns 0.

  // We do not allow players to pick a zone they do not own to be their capital.
  for(let i = 1; i <= game.players; i++) {
    if( game.required & (1<<i) ) {    // This player has a pick capital this turn.
      let pickID = turns[i] ? turns[i].pick : 0;
      if( !pickID || pickID <= 0 || pickID > game.zones || !bfH.isLand(game, pickID) ||
          game.zoneOwner[pickID] !== i) {
        // Pick was invalid or missing, select a default one.
        pickID = generatePickCapital(game, i).pick;
      }
      if( !pickID || pickID <= 0 || pickID > game.zones || !bfH.isLand(game, pickID) ||
          game.zoneOwner[pickID] !== i) {
        console.log('BAD: Default capital pick was still bad (' + pickID + ').');
      }
      else {
        const pos = getNewZoneXY(game, pickID);
        bfTurnReplay.pickCapital(game, i, pickID, pos.x, pos.y);
        game.capital[i] = pickID;
        game.capitalX[i] = pos.x;
        game.capitalY[i] = pos.y;
      }
    }
  }

  // Progress to next turn:
  game.turn = BF.TURN_NORMAL;
  assignAutomaticCapitalForces(game);
  collectIncome(game, true);
  setMaxBuy(game);
  setTurnRequiredFlags(game);
  setTurnTimeLimit(game);
  return 0;  // success.
}


function doNormalTurn (game: Game, turns: PlayerNormalTurn[]): number {
  // Always successful and always returns 0.

  // Reset previous zone owner (all and 0 zone):
  for(let i = 0; i <= game.zones; i++) {
    game.zoneOwnerPrev[i] = game.zoneOwner[i];
  }

  // Remove forces that were flagged as destroyed last turn:
  for(let i = 0; i <= game.players; i++) {  // All players and 0 player.
    for(let j = game.force[i].length - 1; j >= 0; j--) {
      let zoneID = game.force[i][j][BF.FORCE_INDEX_ZONE];
      if( zoneID === BF.FORCE_ZONE_DESTROYED ||
          zoneID === BF.FORCE_ZONE_CRASH ||
          zoneID === BF.FORCE_ZONE_SPLASH ) {
        game.force[i].splice(j, 1);  // Remove the force.
      }
    }
  }

  // Reset previous force locations:
  for(let i = 0; i <= game.players; i++) {  // All players and 0 player.
    for(let j = 0; j < game.force[i].length; j++) {
      game.force[i][j][BF.FORCE_INDEX_ZONE_PREV] = game.force[i][j][BF.FORCE_INDEX_ZONE];
      game.force[i][j][BF.FORCE_INDEX_X_PREV] = game.force[i][j][BF.FORCE_INDEX_X];
      game.force[i][j][BF.FORCE_INDEX_Y_PREV] = game.force[i][j][BF.FORCE_INDEX_Y];
    }
  } 

  doArrange(game, turns);
  doMove(game, turns);
  doCombat(game);
  doEliminateAllMoveTerminateForces(game);
  doTakeover(game);
  doAircraftLanding(game, turns);
  doPlayerElimination(game);
  doRetreatValuesCleanUp(game);
  checkForVictory(game);
  if( !game.replay.victory ) {   // No victory yet.
    doTurnPurchase(game, turns);
    setAllMovementPoints(game);
    collectIncome(game, false);
  }

  // Progress to next turn (even if it is the last victory one):
  game.turn++;
  setMaxBuy(game);
  setTurnRequiredFlags(game);
  setTurnTimeLimit(game);

  return 0;  // success.
}
